import React from "react";
import { Link } from "react-router-dom";
import { Tab, Tabs } from "@mui/material";

// The default cap for a header tab strip. Below it the strip hugs its content;
// at it the strip locks and the carousel arrows appear. Sized so a busy menu
// (School Profile) reads as a bounded carousel rather than sprawling across the
// header, while a handful of tabs sit at their natural width.
export const TABS_MAX_WIDTH = 560;

// The standard header tab strip: one MUI scrollable Tabs that sizes itself to
// its content up to a maximum width, and shows carousel arrows ONLY when the
// tabs overflow that maximum. Below the cap the strip is exactly as wide as its
// tabs (no arrows, no reserved side space); past it the strip locks to the cap
// and `scrollButtons="auto"` reveals the arrows. This replaces the old
// fixed-width, always-arrows CarouselTabs.
//
// `width: max-content` lets the strip grow to its tabs' natural width; `maxWidth`
// clamps it. Because the clamp gives the scroller a definite width narrower than
// its content, MUI's `auto` scroll buttons appear exactly when (and only when)
// the tabs don't fit - which is the "arrows over a certain number of links,
// omitted when few" behavior, measured by real width rather than a link count.
//
// Arrows are desktop-only (no `allowScrollButtonsMobile`): on a phone the strip
// scrolls by swipe, the expected touch gesture, and the header stays uncluttered.
//
// `items` is [{ key, label, onClick?, to?, href?, disabled? }]; `value` is the
// active tab index (or false for none). A tab navigates via whichever it
// carries: `to` (in-app router Link), `href` (full page load), or `onClick`
// (callback / local state). The active tab can omit its handler.
export default function HeaderTabs({
  items,
  value,
  maxWidth = TABS_MAX_WIDTH,
  sx,
}) {
  return (
    <Tabs
      value={value}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        // Hug the tabs' content width, but never exceed the cap. The clamp is
        // what makes the scroller narrower than its content and so triggers the
        // auto arrows; without it the strip would just grow forever.
        width: "max-content",
        maxWidth: { md: maxWidth },
        minWidth: 0,
        // Ride the header's bottom border (callers used to pass this in sx).
        mb: "-1px",
        // Keep the end-arrows in place but dimmed when disabled, so the strip
        // width stays stable rather than jumping as an arrow hides at an end.
        "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
        // Centre the tabs within the strip when they fit; a no-op once they
        // overflow and the carousel scrolls. Pinning the tabs to no-shrink keeps
        // them at their natural width so they overflow (and scroll) rather than
        // squishing to fit, which would defeat both the centring and the arrows.
        "& .MuiTabs-flexContainer": { justifyContent: "center" },
        "& .MuiTab-root": { flexShrink: 0 },
        ...sx,
      }}
    >
      {items.map((tab) => {
        // Route through whichever the item carries: an in-app router Link, a
        // plain anchor (full page load), or neither (a plain onClick button).
        let nav = {};
        if (tab.to) nav = { component: Link, to: tab.to };
        else if (tab.href) nav = { component: "a", href: tab.href };
        return (
          <Tab
            key={tab.key}
            label={tab.label}
            onClick={tab.onClick}
            disabled={tab.disabled}
            {...nav}
          />
        );
      })}
    </Tabs>
  );
}
